// authConfig.ts - Simple Authentication Configuration

import { getDeployEnvironment } from './deployEnv';

const toBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return defaultValue;
};

const toPositiveNumber = (value: string | undefined, defaultValue: number): number => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return defaultValue;
};

// Keycloak enable/disable flag
export const KEYCLOAK_ENABLED = toBoolean(process.env.REACT_APP_KEYCLOAK_ENABLED, false);
export const KEYCLOAK_REFRESH_MIN_VALIDITY_SECONDS = toPositiveNumber(
  process.env.REACT_APP_KEYCLOAK_REFRESH_MIN_VALIDITY_SECONDS ??
    process.env.REACT_APP_KEYCLOAK_TOKEN_MIN_VALIDITY_SECONDS,
  60
);
export const KEYCLOAK_REFRESH_CHECK_INTERVAL_MS = toPositiveNumber(
  process.env.REACT_APP_KEYCLOAK_REFRESH_CHECK_INTERVAL_MS ??
    process.env.REACT_APP_KEYCLOAK_REFRESH_INTERVAL_MS,
  30000
);
export const KEYCLOAK_TOKEN_MIN_VALIDITY_SECONDS = KEYCLOAK_REFRESH_MIN_VALIDITY_SECONDS;
export const KEYCLOAK_REFRESH_INTERVAL_MS = KEYCLOAK_REFRESH_CHECK_INTERVAL_MS;
export const KEYCLOAK_CAPABILITY_ROLES_ENABLED = toBoolean(
  process.env.REACT_APP_SECURITY_CAPABILITY_KEYCLOAK_ROLES_ENABLED ??
    process.env.REACT_APP_KEYCLOAK_CAPABILITY_ROLES_ENABLED,
  false
);
export const BACKEND_CAPABILITY_SOURCE_ENABLED = toBoolean(
  process.env.REACT_APP_USE_BACKEND_CAPABILITIES,
  !KEYCLOAK_CAPABILITY_ROLES_ENABLED
);

// Keycloak configuration
export const KEYCLOAK_CONFIG = {
  // Production config
  production: {
    url: process.env.REACT_APP_KEYCLOAK_PRODUCTION_URL || 'https://intelevateapps.halliburton.com',
    realm: process.env.REACT_APP_KEYCLOAK_PRODUCTION_REALM || 'Intelift',
    clientId: process.env.REACT_APP_KEYCLOAK_PRODUCTION_CLIENT_ID || 'DMS',
  },
  // Staging config
  staging: {
    url: process.env.REACT_APP_KEYCLOAK_STAGING_URL || 'https://accounts.welllynx.com',
    realm: process.env.REACT_APP_KEYCLOAK_STAGING_REALM || 'Evinsys',
    clientId: process.env.REACT_APP_KEYCLOAK_STAGING_CLIENT_ID || 'dms-staging-frontend',
  },
  // Dedicated development config (server-hosted dev builds)
  development: {
    url:
      process.env.REACT_APP_KEYCLOAK_DEVELOPMENT_URL ||
      process.env.REACT_APP_KEYCLOAK_STAGING_URL ||
      'https://accounts.welllynx.com',
    realm:
      process.env.REACT_APP_KEYCLOAK_DEVELOPMENT_REALM ||
      process.env.REACT_APP_KEYCLOAK_STAGING_REALM ||
      'Evinsys',
    clientId:
      process.env.REACT_APP_KEYCLOAK_DEVELOPMENT_CLIENT_ID ||
      process.env.REACT_APP_KEYCLOAK_STAGING_CLIENT_ID ||
      'DMS',
  },
  // Local/Development config
  local: {
    url: process.env.REACT_APP_KEYCLOAK_LOCAL_URL || 'http://localhost:18080',
    realm: process.env.REACT_APP_KEYCLOAK_LOCAL_REALM || 'dms-local',
    clientId: process.env.REACT_APP_KEYCLOAK_LOCAL_CLIENT_ID || 'dms',
  }
} as const;

// Get Keycloak configuration based on environment
export const getKeycloakConfig = () => {
  const deployEnvironment = getDeployEnvironment();

  if (deployEnvironment === 'local') {
    return KEYCLOAK_CONFIG.local;
  }
  if (deployEnvironment === 'development') {
    return KEYCLOAK_CONFIG.development;
  }
  if (deployEnvironment === 'production') {
    return KEYCLOAK_CONFIG.production;
  }
  return KEYCLOAK_CONFIG.staging;
};

// Check if Keycloak is enabled
export const isKeycloakEnabled = (): boolean => {
  return KEYCLOAK_ENABLED;
};

export const getKeycloakRefreshMinValiditySeconds = (): number => {
  return KEYCLOAK_REFRESH_MIN_VALIDITY_SECONDS;
};

export const getKeycloakRefreshIntervalMs = (): number => {
  return KEYCLOAK_REFRESH_CHECK_INTERVAL_MS;
};

export const isBackendCapabilitySourceEnabled = (): boolean => {
  return BACKEND_CAPABILITY_SOURCE_ENABLED;
};

// Get authentication mode
export const getAuthMode = (): 'keycloak' | 'backend' => {
  return isKeycloakEnabled() ? 'keycloak' : 'backend';
};

// Type definitions
export type AuthMode = 'keycloak' | 'backend';
export type KeycloakConfig = typeof KEYCLOAK_CONFIG.local;
